// src/components/dashboards/FeeStructures.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { financeAPI } from '../../services/api';

const fmt = (n) =>
  n !== null && n !== undefined
    ? `₹${parseFloat(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

const FREQUENCY_CHOICES = [
  { value: 'YEARLY',   label: 'Yearly' },
  { value: 'ONE_TIME', label: 'One-Time' },
  { value: 'SEMESTER', label: 'Semester-wise' },
  { value: 'TERM',     label: 'Term-wise' },
  { value: 'MONTHLY',  label: 'Monthly' },
];

const STANDARD_FEE_HEADS = [
  'Tuition / Academic Fee',
  'Admission Fee',
  'Registration Fee',
  'University Fee',
  'Examination Fee',
  'Library Fee',
  'Laboratory Fee',
  'Skills Lab Fee',
  'Development Fee',
  'Student Welfare Fee',
  'Sports / Gymkhana Fee',
  'Miscellaneous Fee',
  'Hostel Fee',
  'Mess Fee',
  'Transport Fee',
  'Caution / Security Deposit',
];

export default function FeeStructures() {
  const [structures, setStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Selected state
  const [selectedCourse, setSelectedCourse] = useState('MBBS');
  const [selectedYear, setSelectedYear] = useState(1);

  // Active working fee structure form state
  const [activeStruct, setActiveStruct] = useState(null);
  const [components, setComponents] = useState([]);
  const [deadlineDays, setDeadlineDays] = useState(30);
  const [isSaving, setIsSaving] = useState(false);

  // Custom component modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newHeadName, setNewHeadName] = useState('');
  const [newHeadAmount, setNewHeadAmount] = useState('');
  const [newHeadFreq, setNewHeadFreq] = useState('YEARLY');
  const [newHeadRefundable, setNewHeadRefundable] = useState(false);
  const [newHeadMandatory, setNewHeadMandatory] = useState(true);

  const fetchStructures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await financeAPI.getAllFeeStructures();
      const list = Array.isArray(res) ? res : res?.results || [];
      setStructures(list);
    } catch (err) {
      console.error('Failed to fetch fee structures:', err);
      setError('Could not load fee structures. Please check backend connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStructures();
  }, [fetchStructures]);

  // Sync activeStruct when selection or structures change
  useEffect(() => {
    if (!structures || structures.length === 0) return;

    let target = null;
    if (selectedCourse === 'MBBS') {
      target = structures.find(
        (s) => s.course === 'MBBS' && (parseInt(s.mbbs_year, 10) === parseInt(selectedYear, 10))
      );
    } else {
      target = structures.find((s) => s.course === selectedCourse && !s.mbbs_year);
    }

    if (target) {
      setActiveStruct(target);
      setComponents(Array.isArray(target.components) ? target.components : []);
      setDeadlineDays(target.default_deadline_days || 30);
    } else {
      setActiveStruct(null);
      setComponents([]);
      setDeadlineDays(30);
    }
  }, [structures, selectedCourse, selectedYear]);

  // Calculate total live sum
  const calculateLiveTotal = () => {
    return components.reduce((sum, c) => {
      if (c && c.is_active !== false) {
        return sum + (parseFloat(c.amount) || 0);
      }
      return sum;
    }, 0);
  };

  const handleComponentChange = (index, field, value) => {
    setComponents((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleToggleComponentActive = (index) => {
    setComponents((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], is_active: !next[index].is_active };
      return next;
    });
  };

  const handleRemoveComponent = (index) => {
    setComponents((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddCustomHead = () => {
    if (!newHeadName.trim()) return;
    const newItem = {
      id: `custom_${Date.now()}`,
      name: newHeadName.trim(),
      amount: parseFloat(newHeadAmount) || 0,
      frequency: newHeadFreq,
      is_refundable: newHeadRefundable,
      is_mandatory: newHeadMandatory,
      is_active: true,
    };
    setComponents((prev) => [...prev, newItem]);
    setNewHeadName('');
    setNewHeadAmount('');
    setNewHeadFreq('YEARLY');
    setNewHeadRefundable(false);
    setNewHeadMandatory(true);
    setShowAddModal(false);
  };

  const handleSaveStructure = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMsg('');

      const totalFee = calculateLiveTotal();
      const payload = {
        course: selectedCourse,
        mbbs_year: selectedCourse === 'MBBS' ? parseInt(selectedYear, 10) : null,
        year_label:
          selectedCourse === 'MBBS'
            ? `${selectedYear}${selectedYear === 1 ? 'st' : selectedYear === 2 ? 'nd' : selectedYear === 3 ? 'rd' : 'th'} Year`
            : selectedCourse,
        tuition_fee: totalFee,
        total_fee: totalFee,
        default_deadline_days: parseInt(deadlineDays, 10) || 30,
        components: components,
        is_active: true,
      };

      if (activeStruct && activeStruct.id) {
        await financeAPI.updateFeeStructureDetail(activeStruct.id, payload);
      } else {
        await financeAPI.createFeeStructure(payload);
      }

      setSuccessMsg('Fee structure saved and propagated to active students successfully!');
      fetchStructures();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Failed to save fee structure:', err);
      setError(err?.response?.data?.error || 'Failed to save fee structure.');
    } finally {
      setIsSaving(false);
    }
  };

  const liveTotal = calculateLiveTotal();

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
        Loading Fee Structures...
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", color: '#111827' }}>

      {/* Header & Course Selection Bar */}
      <div
        style={{
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 24,
          background: '#fff',
          padding: '16px 24px',
          borderRadius: 14,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#1f2937' }}>
            🎓 Student Fee Structures
          </h2>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0 0' }}>
            Configure course & year-wise fee heads. Updates automatically sync to student accounts.
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 4 }}>
              Course
            </label>
            <select
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontSize: 14,
                fontWeight: 600,
                background: '#f9fafb',
              }}
            >
              <option value="MBBS">MBBS</option>
              <option value="PG">Post Graduate (PG)</option>
              <option value="SUPER_SPECIALITY">Super Speciality (SSPG)</option>
              <option value="INTERNSHIP">Internship</option>
            </select>
          </div>

          {selectedCourse === 'MBBS' && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#4b5563', display: 'block', marginBottom: 4 }}>
                Academic Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  fontSize: 14,
                  fontWeight: 600,
                  background: '#f9fafb',
                }}
              >
                <option value={1}>1st Year (Pre-clinical)</option>
                <option value={2}>2nd Year (Para-clinical)</option>
                <option value={3}>3rd Year (Clinical)</option>
                <option value={4}>4th Year (Final MBBS)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div
          style={{
            padding: '12px 18px',
            borderRadius: 10,
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            marginBottom: 20,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {error}
        </div>
      )}
      {successMsg && (
        <div
          style={{
            padding: '12px 18px',
            borderRadius: 10,
            background: '#f0fdf4',
            border: '1px solid #bbf7d0',
            color: '#16a34a',
            marginBottom: 20,
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {successMsg}
        </div>
      )}

      {/* Summary Banner Card */}
      <div
        style={{
          background: '#ffffff',
          color: '#111827',
          padding: 24,
          borderRadius: 16,
          marginBottom: 24,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
          display: 'flex',
          justify: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 20,
        }}
      >
        <div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 1,
              color: '#4f46e5',
            }}
          >
            {selectedCourse} {selectedCourse === 'MBBS' ? `Year ${selectedYear}` : ''} Structure
          </span>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '4px 0 0 0', color: '#111827' }}>
            {fmt(liveTotal)}
          </h1>
          <p style={{ fontSize: 13, color: '#4b5563', margin: '4px 0 0 0' }}>
            Total calculated fee based on {components.filter((c) => c.is_active !== false).length} active fee head(s).
          </p>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: '10px 18px',
              borderRadius: 10,
              border: '1px solid #4f46e5',
              background: '#fff',
              color: '#4f46e5',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14,
              transition: 'all .2s',
            }}
          >
            + Add Fee Head
          </button>
          <button
            onClick={handleSaveStructure}
            disabled={isSaving}
            style={{
              padding: '10px 22px',
              borderRadius: 10,
              border: 'none',
              background: '#4f46e5',
              color: '#fff',
              fontWeight: 700,
              cursor: isSaving ? 'not-allowed' : 'pointer',
              fontSize: 14,
              boxShadow: '0 2px 10px rgba(79,70,229,0.2)',
              opacity: isSaving ? 0.7 : 1,
            }}
          >
            {isSaving ? 'Saving...' : 'Save & Sync Dues'}
          </button>
        </div>
      </div>

      {/* Main Fee Components Table Card */}
      <div
        style={{
          background: '#fff',
          borderRadius: 14,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
          overflow: 'hidden',
          marginBottom: 24,
        }}
      >
        <div
          style={{
            padding: '16px 20px',
            background: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justify: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: '#374151' }}>
            Fee Component Breakdown
          </h3>
          <span style={{ fontSize: 13, color: '#6b7280' }}>
            Payment Deadline:
            <input
              type="number"
              value={deadlineDays}
              onChange={(e) => setDeadlineDays(e.target.value)}
              style={{
                width: 60,
                marginLeft: 8,
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontSize: 13,
              }}
            />{' '}
            days from enrollment
          </span>
        </div>

        {components.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
            No fee components added yet for this structure. Click <strong>+ Add Fee Head</strong> to start.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <th style={thStyle}>Fee Head</th>
                <th style={thStyle}>Amount (₹)</th>
                <th style={thStyle}>Frequency</th>
                <th style={thStyle}>Refundable</th>
                <th style={thStyle}>Mandatory</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {components.map((comp, idx) => (
                <tr
                  key={comp.id || idx}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    opacity: comp.is_active !== false ? 1 : 0.5,
                    background: comp.is_active !== false ? '#fff' : '#f9fafb',
                  }}
                >
                  {/* Name */}
                  <td style={tdStyle}>
                    <input
                      type="text"
                      value={comp.name}
                      onChange={(e) => handleComponentChange(idx, 'name', e.target.value)}
                      style={inputStyle}
                    />
                  </td>

                  {/* Amount */}
                  <td style={tdStyle}>
                    <input
                      type="number"
                      value={comp.amount}
                      onChange={(e) => handleComponentChange(idx, 'amount', e.target.value)}
                      style={{ ...inputStyle, fontWeight: 700, color: '#1e1b4b', width: 120 }}
                    />
                  </td>

                  {/* Frequency */}
                  <td style={tdStyle}>
                    <select
                      value={comp.frequency || 'YEARLY'}
                      onChange={(e) => handleComponentChange(idx, 'frequency', e.target.value)}
                      style={selectStyle}
                    >
                      {FREQUENCY_CHOICES.map((f) => (
                        <option key={f.value} value={f.value}>
                          {f.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  {/* Refundable */}
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleComponentChange(idx, 'is_refundable', !comp.is_refundable)}
                      style={badgeBtnStyle(comp.is_refundable ? '#dbeafe' : '#f3f4f6', comp.is_refundable ? '#1d4ed8' : '#6b7280')}
                    >
                      {comp.is_refundable ? 'Refundable' : 'Non-refundable'}
                    </button>
                  </td>

                  {/* Mandatory */}
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleComponentChange(idx, 'is_mandatory', !comp.is_mandatory)}
                      style={badgeBtnStyle(comp.is_mandatory !== false ? '#fef3c7' : '#f3f4f6', comp.is_mandatory !== false ? '#b45309' : '#6b7280')}
                    >
                      {comp.is_mandatory !== false ? 'Mandatory' : 'Optional'}
                    </button>
                  </td>

                  {/* Status Toggle */}
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleToggleComponentActive(idx)}
                      style={badgeBtnStyle(comp.is_active !== false ? '#dcfce7' : '#fee2e2', comp.is_active !== false ? '#15803d' : '#b91c1c')}
                    >
                      {comp.is_active !== false ? 'Active' : 'Disabled'}
                    </button>
                  </td>

                  {/* Actions */}
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    <button
                      onClick={() => handleRemoveComponent(idx)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: 6,
                        border: '1px solid #fecaca',
                        background: '#fff',
                        color: '#dc2626',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Custom Fee Head Modal */}
      {showAddModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 16px 0', color: '#111827' }}>
              Add New Fee Component
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={labelStyle}>Fee Head Name</label>
                <input
                  type="text"
                  placeholder="e.g. Skill Development Fee"
                  value={newHeadName}
                  onChange={(e) => setNewHeadName(e.target.value)}
                  list="standard-heads"
                  style={inputModalStyle}
                />
                <datalist id="standard-heads">
                  {STANDARD_FEE_HEADS.map((head) => (
                    <option key={head} value={head} />
                  ))}
                </datalist>
              </div>

              <div>
                <label style={labelStyle}>Amount (₹)</label>
                <input
                  type="number"
                  placeholder="0.00"
                  value={newHeadAmount}
                  onChange={(e) => setNewHeadAmount(e.target.value)}
                  style={inputModalStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Billing Frequency</label>
                <select
                  value={newHeadFreq}
                  onChange={(e) => setNewHeadFreq(e.target.value)}
                  style={inputModalStyle}
                >
                  {FREQUENCY_CHOICES.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', gap: 20, marginTop: 4 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newHeadRefundable}
                    onChange={(e) => setNewHeadRefundable(e.target.checked)}
                  />
                  Refundable
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={newHeadMandatory}
                    onChange={(e) => setNewHeadMandatory(e.target.checked)}
                  />
                  Mandatory
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24 }}>
              <button
                onClick={() => setShowAddModal(false)}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomHead}
                style={{
                  padding: '8px 18px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#4f46e5',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Add Head
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const tdStyle = {
  padding: '12px 16px',
  fontSize: 14,
};

const inputStyle = {
  width: '100%',
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 14,
};

const selectStyle = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: 13,
  background: '#fff',
};

const badgeBtnStyle = (bg, color) => ({
  padding: '4px 10px',
  borderRadius: 20,
  border: 'none',
  background: bg,
  color: color,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
});

const labelStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: '#374151',
  display: 'block',
  marginBottom: 4,
};

const inputModalStyle = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid #d1d5db',
  fontSize: 14,
};
